import * as turf from '@turf/turf'
// eslint-disable-next-line boundaries/entry-point
import { createPolygonsForWrapping } from 'features/assetViz/objects/surfaces/util/clippingUtils'
// eslint-disable-next-line boundaries/entry-point
import { ComponentSurfaceWrappingMode } from 'features/assetViz/objects/surfaces/util/SurfaceProvider'
import { isNil } from 'lodash'
import {
  GeckoAssetModel,
  GeckoComponentModelType,
  GeckoComponentPolygon,
  GeckoComponentRect,
  GeckoLegacyBoilerRepairRegion,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoPlate,
  GeckoPolygonalPlate,
} from 'types'

const XY_UNIT_TO_USE = GeckoMeasurementUnit.Meters

/**
 * A function that gets all repairs for a given component.
 */
export function getAllRepairsForComponent(
  assetModel: GeckoAssetModel,
  componentSlug: string
): GeckoLegacyBoilerRepairRegion[] | GeckoPlate[] | GeckoPolygonalPlate[] {
  const component = assetModel.data.components.get(componentSlug)
  if (!component) {
    throw new Error('Component not found on asset model')
  }

  switch (component.type) {
    // -- Plate components
    case GeckoComponentModelType.VerticalTankShell:
      return component.platesArray
    case GeckoComponentModelType.TankFloor:
      return component.platesArray
    case GeckoComponentModelType.TankFlatRoof:
      return component.platesArray
    case GeckoComponentModelType.ShipDeck:
      return component.polygonalPlatesArray
    case GeckoComponentModelType.BasicShipHull:
      return component.platesArray

    // -- Boiler components
    case GeckoComponentModelType.LegacyBoilerWalls:
      return component.repairRegionsArray

    // -- Repairs not supported
    case GeckoComponentModelType.HorizontalTankShell:
      console.warn('Horizontal tank shell repairs not supported')
      return []
    case GeckoComponentModelType.LegacyCone:
      console.warn('Legacy cone repairs not supported')
      return []
    case GeckoComponentModelType.Cone:
      console.warn('Cone repairs not supported')
      return []
    case GeckoComponentModelType.LaunchTube:
      console.warn('Launch tube repairs not supported')
      return []
    case GeckoComponentModelType.PipeSection:
      console.warn('Pipe section repairs not supported')
      return []
    case GeckoComponentModelType.Dome:
      console.warn('Dome repairs not supported')
      return []
    case GeckoComponentModelType.DomeWall:
      console.warn('Dome wall repairs not supported')
      return []
    case GeckoComponentModelType.FlatCircularFloor:
      console.warn('Flat circular floor repairs not supported')
      return []
    case GeckoComponentModelType.VerticalTankFoundation:
      console.warn('Vertical tank foundation repairs not supported')
      return []
    case GeckoComponentModelType.MachinedForgingSimple:
      console.warn('Machined forging simple repairs not supported')
      return []
    case GeckoComponentModelType.PressureVesselShell:
      console.warn('Pressure vessel shell repairs not supported')
      return []
    case GeckoComponentModelType.HemisphericalHead:
      console.warn('Hemispherical head repairs not supported')
      return []
    case GeckoComponentModelType.TorisphericalHead:
      console.warn('Torispherical head repairs not supported')
      return []
    case GeckoComponentModelType.EllipsoidalHead:
      console.warn('Ellipsoidal head repairs not supported')
      return []
  }
}

/**
 * A function that gets the most recent plate, if any, from `allPlates` that covers the supplied `coords`.
 */
export function getMostRecentPlateInBin(
  allPlates: GeckoPlate[],
  binBounds: GeckoComponentRect,
  latestDateToUse: Date | undefined
): GeckoPlate | undefined {
  return (
    allPlates
      .filter((plate) => {
        return (
          (isNil(latestDateToUse)
            ? true
            : isNil(plate.installationDate)
              ? true
              : plate.installationDate <= latestDateToUse) &&
          GeckoComponentRect.Overlaps(plate.geometry, binBounds)
        )
      })
      // Sort by installation date, with ties broken by wall thickness
      .toSorted((a, b) => {
        // prioritize the thinnest plates
        // TODO (BEFORE MERGE): @Patrick and @Tom M believe this should be thickest, but that messes with our test results.
        //    Double check with them + PMs that we want that changed.
        if (!a.wallNominalThickness && !b.wallNominalThickness) return 0
        if (!a.wallNominalThickness) return 1
        if (!b.wallNominalThickness) return -1
        return a.wallNominalThickness.lt(b.wallNominalThickness) ? -1 : 1
      })
      .toSorted((a, b) => {
        // prioritize most recent installation
        if (!a.installationDate && !b.installationDate) return 0
        if (!a.installationDate) return 1
        if (!b.installationDate) return -1
        return a.installationDate > b.installationDate ? -1 : 1
      })
      .at(0)
  )
}

export function isPointInPolygonPlate(
  binBounds: GeckoComponentRect,
  plate: GeckoPolygonalPlate
) {
  const xStart = binBounds.x.start.toNumber(XY_UNIT_TO_USE)
  const yStart = binBounds.y.start.toNumber(XY_UNIT_TO_USE)
  const xEnd = binBounds.x.end.toNumber(XY_UNIT_TO_USE)
  const yEnd = binBounds.y.end.toNumber(XY_UNIT_TO_USE)
  const bin_coords = [
    [xStart, yStart],
    [xEnd, yEnd],
    [xStart, yEnd],
    [xEnd, yStart],
  ]
  let isInside: boolean | undefined = undefined
  for (const point of bin_coords) {
    const plate_object = turf.polygon([
      plate.geometry.map((point) => [
        point.x.toNumber(XY_UNIT_TO_USE),
        point.y.toNumber(XY_UNIT_TO_USE),
      ]),
    ])
    const bin_point = turf.point(point)
    isInside =
      (isInside || isNil(isInside)) &&
      turf.booleanPointInPolygon(bin_point, plate_object)
  }
  return !!isInside
}

function checkWrappingPlate(
  plate: GeckoPolygonalPlate,
  surfaceBounds: Readonly<GeckoComponentRect>,
  surfaceWrapMode: ComponentSurfaceWrappingMode
): GeckoPolygonalPlate[] | undefined {
  const boundsXStart = surfaceBounds.x.start.toNumber(XY_UNIT_TO_USE)
  const boundsXSpan = surfaceBounds.x.delta.toNumber(XY_UNIT_TO_USE)
  const boundsYStart = surfaceBounds.y.start.toNumber(XY_UNIT_TO_USE)
  const boundsYSpan = surfaceBounds.y.delta.toNumber(XY_UNIT_TO_USE)

  const uvs = plate.geometry
    .map((point) => {
      return [
        (point.x.toNumber(XY_UNIT_TO_USE) - boundsXStart) / boundsXSpan,
        (point.y.toNumber(XY_UNIT_TO_USE) - boundsYStart) / boundsYSpan,
      ]
    })
    .flat()

  const wrapsX =
    surfaceWrapMode === ComponentSurfaceWrappingMode.UnrolledComponentWrapsX ||
    surfaceWrapMode === ComponentSurfaceWrappingMode.ComponentWrapsX

  const wrapsY =
    surfaceWrapMode === ComponentSurfaceWrappingMode.UnrolledComponentWrapsY ||
    surfaceWrapMode === ComponentSurfaceWrappingMode.ComponentWrapsY

  const ComponentPolygon = GeckoComponentPolygon.CreateFromPolygonalPlate(
    plate.geometry,
    surfaceBounds,
    wrapsX,
    wrapsY
  )
  const pathDirectionX = ComponentPolygon.pathDirectionX
  const pathDirectionY = ComponentPolygon.pathDirectionY
  const WrappedPolygons = createPolygonsForWrapping(
    uvs,
    pathDirectionX,
    pathDirectionY,
    wrapsX,
    wrapsY
  )

  // Convert each wrapped UV polygon back to Measurement coordinates
  const convertedGeometries: { x: GeckoMeasurement; y: GeckoMeasurement }[][] =
    WrappedPolygons.map(
      (
        wrappedPolygon: number[]
      ): { x: GeckoMeasurement; y: GeckoMeasurement }[] => {
        const singleConvertedGeometry: {
          x: GeckoMeasurement
          y: GeckoMeasurement
        }[] = []
        for (let i = 0; i < wrappedPolygon.length; i += 2) {
          const u = wrappedPolygon[i]
          const v = wrappedPolygon[i + 1]
          if (u === undefined || v === undefined) {
            console.warn('Missing UV coordinate in wrapped polygon')
            continue // Skip this coordinate pair
          }
          singleConvertedGeometry.push({
            x: GeckoMeasurement.CreateFromValue(
              u * boundsXSpan + boundsXStart,
              XY_UNIT_TO_USE
            ),
            y: GeckoMeasurement.CreateFromValue(
              v * boundsYSpan + boundsYStart,
              XY_UNIT_TO_USE
            ),
          })
        }
        return singleConvertedGeometry
      }
    )

  // Create new Plate objects using the converted geometries
  const newPlates: GeckoPolygonalPlate[] = convertedGeometries.map(
    (newGeometry, index) => {
      // Use the constructor to create a new instance
      return new GeckoPolygonalPlate(
        `${plate.id}-split-${index}`, // new ID
        plate.name,
        newGeometry, // new geometry
        plate.polygonUnits,
        plate.wallNominalThickness,
        plate.coatingNominalThickness,
        plate.installationDate
      )
    }
  )
  // Return the array of new plates
  return newPlates
}

/**
 * Checks how many corners of a rectangular bin fall inside a polygonal plate or its wrapped parts.
 * @param binBounds The rectangular bounds of the bin.
 * @param single_plate The polygonal plate to check against.
 * @param surfaceBounds Optional: The surface bounds for UV conversion and wrapping, if applicable.
 * @param surfaceWrapMode Optional: The surface wrap mode, if applicable.
 * @returns 0 if no corners are inside, 1 if 1-3 corners are inside, 2 if all 4 corners are inside.
 */
function checkBinPlateOverlap(
  binBounds: GeckoComponentRect,
  single_plate: GeckoPolygonalPlate,
  surfaceBounds?: Readonly<GeckoComponentRect>,
  surfaceWrapMode?: ComponentSurfaceWrappingMode
): number {
  const xStartBin = binBounds.x.start.toNumber(XY_UNIT_TO_USE)
  const yStartBin = binBounds.y.start.toNumber(XY_UNIT_TO_USE)
  const xEndBin = binBounds.x.end.toNumber(XY_UNIT_TO_USE)
  const yEndBin = binBounds.y.end.toNumber(XY_UNIT_TO_USE)
  const bin_coords = [
    [xStartBin, yStartBin],
    [xEndBin, yStartBin],
    [xEndBin, yEndBin],
    [xStartBin, yEndBin],
  ]

  if (!single_plate.geometry || single_plate.geometry.length < 3) {
    console.warn(
      'Plate has invalid geometry for overlap check:',
      single_plate.id
    )
    return 0
  }

  let platesToCheck: GeckoPolygonalPlate[] = [single_plate] // Default to the original plate

  // Check if wrapping is applicable and parameters are provided
  if (
    surfaceBounds &&
    surfaceWrapMode !== undefined &&
    (surfaceWrapMode === ComponentSurfaceWrappingMode.UnrolledComponentWrapsX ||
      surfaceWrapMode === ComponentSurfaceWrappingMode.ComponentWrapsX ||
      surfaceWrapMode ===
        ComponentSurfaceWrappingMode.UnrolledComponentWrapsY ||
      surfaceWrapMode === ComponentSurfaceWrappingMode.ComponentWrapsY)
  ) {
    const wrappedPlatesResult = checkWrappingPlate(
      single_plate,
      surfaceBounds,
      surfaceWrapMode
    )
    if (wrappedPlatesResult && wrappedPlatesResult.length > 0) {
      platesToCheck = wrappedPlatesResult
    } else {
      // If wrapping fails or results in no plates, we might revert to checking the original plate
      // or consider it as no overlap depending on requirements.
      // console.warn('Wrapping for overlap check resulted in no plates. Checking original.');
      // platesToCheck remains [single_plate] or could be set to [] if no overlap should be reported.
    }
  }

  let max_score = 0
  for (const platePart of platesToCheck) {
    let pointsInside = 0
    try {
      // Create the plate polygon coordinates array
      const platePolygonCoords = platePart.geometry.map((point) => [
        point.x.toNumber(XY_UNIT_TO_USE),
        point.y.toNumber(XY_UNIT_TO_USE),
      ])

      // Ensure the polygon is closed for turf.booleanPointInPolygon
      // Check if the first and last points are the same
      const firstPoint = platePolygonCoords[0]
      const lastPoint = platePolygonCoords[platePolygonCoords.length - 1]
      if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
        platePolygonCoords.push([...firstPoint]) // Close the polygon by adding the first point to the end
      }

      // Create the turf polygon object
      const plate_object = turf.polygon([platePolygonCoords])

      // Check each bin corner against the plate polygon
      for (const pointCoords of bin_coords) {
        const bin_point = turf.point(pointCoords)
        // Use booleanPointInPolygon which handles points on the boundary correctly
        if (turf.booleanPointInPolygon(bin_point, plate_object)) {
          pointsInside++
        }
      }
    } catch (error) {
      // Log error and treat as no overlap to prevent crashes
      console.error(
        'Error during turf.booleanPointInPolygon check:',
        error,
        'Plate ID:',
        platePart.id
      )
      return 0
    }
    // Return score based on the number of points inside
    if (pointsInside === 4) {
      max_score = 2 // All corners inside -> full overlap
    } else if (pointsInside > 0) {
      if (max_score < 1) {
        max_score = 1 // 1 to 3 corners inside -> partial overlap
      }
    }
  }
  return max_score
}

/**
 * Get the most recent polygonal plate that covers the coordinates of the binBounds
 * @param plates Array of GeckoPolygonalPlate objects to search
 * @param binBounds The bin boundaries to check for overlap
 * @param latestDateToUse Optional cutoff date - only consider plates installed before this date
 * @param surfaceBounds Optional: The surface bounds for considering plate wrapping during overlap scoring and for final output processing.
 * @param surfaceWrapMode Optional: The surface wrap mode for considering plate wrapping during overlap scoring and for final output processing.
 * @returns The most recent polygonal plate (potentially split into an array if wrapped) that overlaps with the bin, or undefined if none found.
 */
export function getMostRecentPolygonalPlateInBin(
  plates: GeckoPolygonalPlate[],
  binBounds: GeckoComponentRect,
  latestDateToUse?: Date,
  surfaceBounds?: Readonly<GeckoComponentRect>,
  surfaceWrapMode?: ComponentSurfaceWrappingMode
): GeckoPolygonalPlate | undefined {
  const candidate_plates = plates
    .map((plate) => ({
      plate,
      overlapScore: checkBinPlateOverlap(
        binBounds,
        plate,
        surfaceBounds,
        surfaceWrapMode
      ),
    }))
    .filter(
      (item) =>
        // Filter 1: Keep if *any* overlap with bin corners (score > 0)
        item.overlapScore > 0 &&
        // Filter 2: Date check (same as before)
        (isNil(latestDateToUse)
          ? true
          : isNil(item.plate.installationDate)
            ? true
            : item.plate.installationDate <= latestDateToUse)
    )
    // Sort candidates based on priority: Overlap > Date > Thickness
    .toSorted((a, b) => {
      // 1. Prioritize full overlap (score 2) over partial (score 1)
      if (a.overlapScore !== b.overlapScore) {
        return b.overlapScore - a.overlapScore // Descending (2 comes before 1)
      }

      // 2. Prioritize most recent installation (if overlap score is the same)
      // Handle null dates correctly: non-null dates come before null dates.
      const dateA = a.plate.installationDate
      const dateB = b.plate.installationDate
      if (dateA && dateB) {
        // Both dates exist, compare them (newest first)
        const timeDiff = dateB.getTime() - dateA.getTime()
        if (timeDiff !== 0) return timeDiff
      } else if (dateA) {
        return -1 // dateA exists, dateB doesn't -> A comes first
      } else if (dateB) {
        return 1 // dateB exists, dateA doesn't -> B comes first
      }
      // If both dates are null or equal, proceed to thickness check.

      // 3. Prioritize thinnest plates (if overlap and date are the same)
      // Handle null thickness correctly: non-null thickness comes before null thickness.
      const thicknessA = a.plate.wallNominalThickness
      const thicknessB = b.plate.wallNominalThickness
      if (thicknessA && thicknessB) {
        // Both thicknesses exist, compare them (thinnest first)
        // Assuming .lt() returns true if A is less than B.
        if (thicknessA.lt(thicknessB)) return -1
        if (thicknessB.lt(thicknessA)) return 1 // Use lt again for symmetry
        return 0 // Thicknesses are equal
      } else if (thicknessA) {
        return -1 // thicknessA exists, thicknessB doesn't -> A comes first
      } else if (thicknessB) {
        return 1 // thicknessB exists, thicknessA doesn't -> B comes first
      }

      // If all criteria are the same (or null), maintain relative order
      return 0
    })
    .at(0) // Select the top candidate after sorting

  const selectedPlate = candidate_plates?.plate // Extract the plate object from the sorted candidate

  // If no plate was found after filtering and sorting, return undefined
  return selectedPlate
}
